import { Play, Rocket } from "lucide-react"
import { cn } from "@/lib/utils"
import { BottomSheet } from "../BottomSheet/BottomSheet"
import { Button } from "../Button/Button"
import { OptimizerConfigRanges } from "../OptimizerConfigRanges/OptimizerConfigRanges"
import type { OptimizerRow } from "../types/optimizer"

export interface OptimizerMobileDrawerMetricLabels {
  return?: string
  sharpe?: string
  profitFactor?: string
  maxDD?: string
  winRate?: string
  trades?: string
  balance?: string
}

export interface OptimizerMobileDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: OptimizerRow | null
  title?: string
  isDeployed?: boolean
  runBacktestLabel?: string
  deployLabel?: string
  deployedLabel?: string
  onRunBacktest?: (row: OptimizerRow) => void
  onDeploy?: (row: OptimizerRow) => void
  metricLabels?: OptimizerMobileDrawerMetricLabels
  className?: string
}

const DEFAULT_METRIC_LABELS: Required<OptimizerMobileDrawerMetricLabels> = {
  return: "Return",
  sharpe: "Sharpe",
  profitFactor: "Profit Factor",
  maxDD: "Max DD",
  winRate: "Win Rate",
  trades: "Trades",
  balance: "Balance",
}

type MetricTone = "success" | "danger" | "neutral"

interface MetricEntry {
  label: string
  value: string
  tone?: MetricTone
  strong?: boolean
}

const TONE_CLASS: Record<MetricTone, string> = {
  success: "text-success",
  danger: "text-danger",
  neutral: "text-foreground",
}

function toMetrics(
  row: OptimizerRow,
  labels: Required<OptimizerMobileDrawerMetricLabels>,
): MetricEntry[] {
  return [
    {
      label: labels.return,
      value: `${row.returnPct >= 0 ? "+" : ""}${row.returnPct.toFixed(1)}%`,
      tone: row.returnPct >= 0 ? "success" : "danger",
      strong: true,
    },
    { label: labels.sharpe, value: row.sharpe.toFixed(2) },
    { label: labels.profitFactor, value: row.profitFactor.toFixed(2) },
    {
      label: labels.maxDD,
      value: `\u2212${Math.abs(row.maxDD).toFixed(1)}%`,
      tone: "danger",
    },
    { label: labels.winRate, value: `${row.winRate.toFixed(1)}%` },
    { label: labels.trades, value: String(row.trades) },
    { label: labels.balance, value: `$${row.finalBalance.toLocaleString()}` },
  ]
}

function hasAnyParams(params: OptimizerRow["params"] | undefined): boolean {
  if (!params) return false
  return Object.values(params).some(
    (entries) => Array.isArray(entries) && entries.length > 0,
  )
}

export function OptimizerMobileDrawer({
  open,
  onOpenChange,
  row,
  title,
  isDeployed = false,
  runBacktestLabel = "Run Visual Backtest",
  deployLabel = "Deploy",
  deployedLabel = "Deployed",
  onRunBacktest,
  onDeploy,
  metricLabels,
  className,
}: OptimizerMobileDrawerProps) {
  const labels = { ...DEFAULT_METRIC_LABELS, ...metricLabels }
  const metrics = row ? toMetrics(row, labels) : []
  const resolvedTitle = title ?? (row ? `Rank #${row.rank}` : "")
  const showFooter = !!(row && (onRunBacktest || onDeploy))
  const showParams = !!row && hasAnyParams(row.params)

  return (
    <BottomSheet
      open={open && row !== null}
      onClose={() => onOpenChange(false)}
      ariaLabel={resolvedTitle || "Optimizer row details"}
      className={className}
    >
      {row && (
        <div className="flex flex-col h-full min-h-0">
          <div className="shrink-0 px-4 pt-2 pb-3">
            <h2 className="text-section text-foreground">{resolvedTitle}</h2>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <div className="flex flex-col gap-2">
              {metrics.map((entry) => (
                <div
                  key={entry.label}
                  className="flex justify-between border-b border-border py-2"
                >
                  <span className="text-label text-muted-foreground">
                    {entry.label}
                  </span>
                  <span
                    className={cn(
                      "text-body",
                      TONE_CLASS[entry.tone ?? "neutral"],
                      entry.strong && "font-semibold",
                    )}
                  >
                    {entry.value}
                  </span>
                </div>
              ))}
            </div>

            {showParams && (
              <div className="mt-4">
                <OptimizerConfigRanges ranges={row.params} />
              </div>
            )}
          </div>

          {showFooter && (
            <div className="shrink-0 flex gap-2 p-4 border-t border-border bg-card">
              {onRunBacktest && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => onRunBacktest(row)}
                >
                  <Play size={12} fill="currentColor" strokeWidth={0} />
                  {runBacktestLabel}
                </Button>
              )}
              {onDeploy && (
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => onDeploy(row)}
                  disabled={isDeployed}
                >
                  <Rocket size={12} />
                  {isDeployed ? deployedLabel : deployLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </BottomSheet>
  )
}
